import { Card, Col, Row, Space, Statistic, Table, Tag, Tooltip, Typography, type TableColumnsType } from 'antd';
import { Link } from 'react-router-dom';
import { formatRupees } from '../../lib/money';
import { adminLinks } from '../../lib/adminLinks';
import { LoadMore } from '../../lib/useCursorPages';
import {
  SKIP_REASON_LABEL,
  formatBps,
  type AgencyBalance,
  type AgencyCommissionRow,
  type AgencyPayoutRow,
  type AgencyRechargeRow,
  type AgencyUserRow,
  type CommissionStatus,
  type CurrencyAmount,
  type PayoutStatus,
} from './agencyApi';

/**
 * Tables and cards shared by the admin agency detail page and the agency
 * portal. `linkUsers` is false in the portal — an agency never opens a user's
 * admin profile.
 */

export const when = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString() : '—');
const day = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString() : '—');

/** Per-currency amounts, never summed across currencies. */
export function Amounts({ rows, empty = '₹0' }: { rows: CurrencyAmount[]; empty?: string }) {
  if (rows.length === 0) return <>{empty}</>;
  return (
    <Space direction="vertical" size={0}>
      {rows.map((r) => (
        <span key={r.currency}>
          {r.currency === 'INR' ? formatRupees(r.amountPaise) : `${r.currency} ${Number(r.amountPaise) / 100}`}
          <Typography.Text type="secondary"> · {r.count.toLocaleString('en-IN')}</Typography.Text>
        </span>
      ))}
    </Space>
  );
}

const inr = (rows: CurrencyAmount[]) => rows.find((r) => r.currency === 'INR')?.amountPaise ?? '0';

export function BalanceCards({ balance }: { balance: AgencyBalance }) {
  const negative = Number(balance.balancePaise) < 0;
  return (
    <Row gutter={[16, 16]}>
      <Col xs={12} md={6}>
        <Card size="small">
          <Statistic title="Payable now" value={formatRupees(balance.payablePaise)} />
        </Card>
      </Col>
      <Col xs={12} md={6}>
        <Card size="small">
          <Statistic
            title={
              <Tooltip title="Commission still inside the hold period (refund cover). Becomes payable when it ends.">
                On hold
              </Tooltip>
            }
            value={formatRupees(balance.heldPaise)}
          />
        </Card>
      </Col>
      <Col xs={12} md={6}>
        <Card size="small">
          <Statistic
            title={
              <Tooltip title="Commission earned minus payouts and refund reversals.">Balance</Tooltip>
            }
            value={formatRupees(balance.balancePaise)}
            valueStyle={negative ? { color: '#cf1322' } : undefined}
          />
          {negative && (
            <Typography.Text type="danger" style={{ fontSize: 12 }}>
              Refunds after a payout — netted against the next payout
            </Typography.Text>
          )}
        </Card>
      </Col>
      <Col xs={12} md={6}>
        <Card size="small">
          <Statistic title="Minimum payout" value={formatRupees(balance.minPayoutPaise)} />
          {balance.tdsBps > 0 && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              TDS {formatBps(balance.tdsBps)} withheld
            </Typography.Text>
          )}
        </Card>
      </Col>
    </Row>
  );
}

/** Earned (accrued) and reversed commission, INR. */
export function earnedPaise(accrued: CurrencyAmount[]): string {
  return inr(accrued);
}

const SOURCE_LABEL: Record<AgencyUserRow['source'], string> = {
  manual: 'Typed code',
  link: 'Invite link',
  admin: 'Assigned by admin',
};

type ListState = { rows: unknown[]; hasMore: boolean; loadingMore: boolean; loadMore: () => void; isLoading: boolean };

export function AgencyUsersTable({
  list,
  rows,
  linkUsers,
}: {
  list: ListState;
  rows: AgencyUserRow[];
  linkUsers: boolean;
}) {
  const columns: TableColumnsType<AgencyUserRow> = [
    {
      title: 'User',
      key: 'user',
      render: (_, r) => {
        const name = (
          <span>
            @{r.handle}
            {r.displayName ? <Typography.Text type="secondary"> · {r.displayName}</Typography.Text> : null}
            {r.deleted ? <Tag style={{ marginLeft: 6 }}>Deleted</Tag> : null}
          </span>
        );
        return linkUsers ? <Link to={adminLinks.user(r.userId)}>{name}</Link> : name;
      },
    },
    { title: 'Joined', dataIndex: 'joinedAt', render: (v: string) => day(v) },
    {
      title: 'Linked',
      key: 'linked',
      render: (_, r) => (
        <span>
          {day(r.attributedAt)} <Typography.Text type="secondary">· {SOURCE_LABEL[r.source]}</Typography.Text>
        </span>
      ),
    },
    {
      title: 'Recharged',
      key: 'recharges',
      render: (_, r) => <Amounts rows={r.recharges} empty="—" />,
    },
  ];
  return (
    <>
      <Table rowKey="userId" size="small" loading={list.isLoading} columns={columns} dataSource={rows} pagination={false} scroll={{ x: 640 }} />
      <LoadMore list={list} noun="users" />
    </>
  );
}

const COMMISSION_TAG: Record<CommissionStatus, { color: string; label: string }> = {
  accrued: { color: 'green', label: 'Earned' },
  reversed: { color: 'red', label: 'Reversed' },
  skipped: { color: 'default', label: 'No commission' },
};

function CommissionCell({ c, now }: { c: AgencyRechargeRow['commission']; now: number }) {
  if (!c) return <Typography.Text type="secondary">Pending (next few minutes)</Typography.Text>;
  if (c.status === 'skipped') {
    return <Tag>{SKIP_REASON_LABEL[c.skipReason ?? ''] ?? 'No commission'}</Tag>;
  }
  const held = c.status === 'accrued' && !c.payoutId && new Date(c.payableAt).getTime() > now;
  return (
    <Space size={4}>
      <span>{formatRupees(c.amountPaise)}</span>
      <Typography.Text type="secondary">({formatBps(c.rateBps)})</Typography.Text>
      {c.status === 'reversed' && <Tag color="red">Reversed</Tag>}
      {held && <Tag color="gold">On hold till {day(c.payableAt)}</Tag>}
      {c.payoutId && c.status === 'accrued' && <Tag color="blue">In payout</Tag>}
    </Space>
  );
}

export function AgencyRechargesTable({
  list,
  rows,
  linkUsers,
}: {
  list: ListState;
  rows: AgencyRechargeRow[];
  linkUsers: boolean;
}) {
  const now = Date.now();
  const columns: TableColumnsType<AgencyRechargeRow> = [
    { title: 'Date', dataIndex: 'capturedAt', render: (v: string | null) => when(v) },
    {
      title: 'User',
      key: 'user',
      render: (_, r) => (linkUsers ? <Link to={adminLinks.user(r.userId)}>@{r.handle}</Link> : `@${r.handle}`),
    },
    { title: 'Package', key: 'pkg', render: (_, r) => r.packageName ?? (r.coins !== null ? `${r.coins} coins` : '—') },
    {
      title: 'Amount',
      key: 'amount',
      render: (_, r) => (
        <span>
          {r.currency === 'INR' ? formatRupees(r.amountPaise) : `${r.currency} ${Number(r.amountPaise) / 100}`}
          {r.status === 'refunded' && <Tag color="red" style={{ marginLeft: 6 }}>Refunded</Tag>}
        </span>
      ),
    },
    { title: 'Paid via', dataIndex: 'gateway', render: (v: string) => (v === 'google_play' ? 'Google Play' : 'Razorpay') },
    { title: 'Commission', key: 'commission', render: (_, r) => <CommissionCell c={r.commission} now={now} /> },
  ];
  return (
    <>
      <Table rowKey="orderId" size="small" loading={list.isLoading} columns={columns} dataSource={rows} pagination={false} scroll={{ x: 800 }} />
      <LoadMore list={list} noun="recharges" />
    </>
  );
}

export function AgencyCommissionsTable({
  list,
  rows,
  linkUsers,
}: {
  list: ListState;
  rows: AgencyCommissionRow[];
  linkUsers: boolean;
}) {
  const now = Date.now();
  const columns: TableColumnsType<AgencyCommissionRow> = [
    { title: 'Recharge date', dataIndex: 'capturedAt', render: (v: string) => when(v) },
    {
      title: 'User',
      key: 'user',
      render: (_, r) =>
        r.handle ? linkUsers ? <Link to={adminLinks.user(r.userId)}>@{r.handle}</Link> : `@${r.handle}` : '—',
    },
    { title: 'Recharge', dataIndex: 'grossPaise', render: (v: string) => formatRupees(v) },
    {
      title: 'Base',
      key: 'base',
      render: (_, r) => (
        <Tooltip title={r.baseMode === 'net_of_tax' ? 'Recharge amount minus GST' : 'Full recharge amount'}>
          {formatRupees(r.basePaise)}
        </Tooltip>
      ),
    },
    { title: 'Rate', dataIndex: 'rateBps', render: (v: number) => formatBps(v) },
    {
      title: 'Commission',
      key: 'amount',
      render: (_, r) => (r.status === 'skipped' ? '—' : formatRupees(r.amountPaise)),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, r) => {
        if (r.status === 'skipped') return <Tag>{SKIP_REASON_LABEL[r.skipReason ?? ''] ?? 'No commission'}</Tag>;
        if (r.status === 'reversed') return <Tag color="red">Reversed {day(r.reversedAt)}</Tag>;
        if (r.payoutId) return <Tag color="blue">In payout</Tag>;
        if (new Date(r.payableAt).getTime() > now) return <Tag color="gold">On hold till {day(r.payableAt)}</Tag>;
        return <Tag color={COMMISSION_TAG.accrued.color}>Payable</Tag>;
      },
    },
  ];
  return (
    <>
      <Table rowKey="id" size="small" loading={list.isLoading} columns={columns} dataSource={rows} pagination={false} scroll={{ x: 900 }} />
      <LoadMore list={list} noun="commission rows" />
    </>
  );
}

export const PAYOUT_TAG: Record<PayoutStatus, { color: string; label: string }> = {
  pending_approval: { color: 'gold', label: 'Awaiting approval' },
  approved: { color: 'blue', label: 'Approved — to pay' },
  processing: { color: 'blue', label: 'Processing' },
  settled: { color: 'green', label: 'Paid' },
  reversed: { color: 'red', label: 'Reversed' },
  rejected: { color: 'red', label: 'Rejected' },
};

export function PayTo({ row }: { row: AgencyPayoutRow }) {
  if (row.payoutMethod === 'upi') return <span>UPI {row.upiId}</span>;
  if (row.payoutMethod === 'bank') {
    return (
      <Space direction="vertical" size={0}>
        <span>{row.accountHolderName}</span>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {row.bankName ? `${row.bankName} · ` : ''}
          {row.accountNumber} · {row.ifsc}
        </Typography.Text>
      </Space>
    );
  }
  return <>—</>;
}

/** Payout history columns; `extra` adds the admin action column. */
export function payoutColumns(
  opts: { showAgency: boolean; extra?: TableColumnsType<AgencyPayoutRow>[number] } = { showAgency: false },
): TableColumnsType<AgencyPayoutRow> {
  return [
    { title: 'Requested', dataIndex: 'createdAt', render: (v: string) => when(v) },
    ...(opts.showAgency
      ? [
          {
            title: 'Agency',
            key: 'agency',
            render: (_: unknown, r: AgencyPayoutRow) =>
              r.agency ? <Link to={adminLinks.agency(r.agency.id, 'payouts')}>{r.agency.name} ({r.agency.code})</Link> : '—',
          },
        ]
      : []),
    { title: 'Amount', dataIndex: 'amountPaise', render: (v: string) => formatRupees(v) },
    {
      title: 'TDS',
      key: 'tds',
      render: (_, r) => (Number(r.tdsPaise) > 0 ? `${formatRupees(r.tdsPaise)} (${formatBps(r.tdsBps)})` : '—'),
    },
    { title: 'To pay', dataIndex: 'netPayoutPaise', render: (v: string) => <strong>{formatRupees(v)}</strong> },
    { title: 'Pay to', key: 'to', render: (_, r) => <PayTo row={r} /> },
    {
      title: 'Status',
      key: 'status',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Tag color={PAYOUT_TAG[r.status].color}>{PAYOUT_TAG[r.status].label}</Tag>
          {r.paymentReference && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Ref {r.paymentReference} · {day(r.settledAt)}
            </Typography.Text>
          )}
          {r.reversalReason && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {r.reversalReason}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    ...(opts.extra ? [opts.extra] : []),
  ];
}
